<script setup lang="ts">
import { computed } from 'vue'
import { routes } from '@app/router/routes'
import Box from '@uiKit/elements/Box.vue'
import Text from '@uiKit/elements/Text.vue'
import PiIcon, { type PiIconName } from '@uiKit/elements/PiIcon.vue'

interface MenuItem {
  label: string
  description: string
  icon: PiIconName
  to: string
}

interface Props {
  isGuest?: boolean
}

const props = withDefaults(defineProps<Props>(), {
  isGuest: false,
})

const allMenuItems: MenuItem[] = [
  {
    label: 'Profile',
    description: 'View & edit your details',
    icon: 'user',
    to: routes.PROFILE,
  },
  {
    label: 'Wallet',
    description: 'Balances, deposits and withdrawals',
    icon: 'wallet',
    to: routes.WALLET,
  },
  {
    label: 'My orders',
    description: 'Track your investment orders',
    icon: 'list-check',
    to: routes.MY_ORDERS,
  },
  {
    label: 'Bank accounts',
    description: 'Manage linked accounts',
    icon: 'wallet',
    to: routes.BANK_ACCOUNTS,
  },
  {
    label: 'Settings',
    description: 'Notifications, preferences',
    icon: 'cog',
    to: routes.SETTINGS,
  },
  {
    label: 'Help & support',
    description: 'FAQs and contact',
    icon: 'comments',
    to: routes.HELP,
  },
]

// Guest users have no orders or bank accounts — hide those items entirely.
const menuItems = computed(() =>
  props.isGuest
    ? allMenuItems.filter(
        (item) =>
          item.to !== routes.MY_ORDERS &&
          item.to !== routes.BANK_ACCOUNTS &&
          item.to !== routes.WALLET,
      )
    : allMenuItems,
)

const emit = defineEmits<{
  (e: 'navigate', to: string): void
  (e: 'logout'): void
}>()

const handleItemActivate = (item: MenuItem): void => {
  emit('navigate', item.to)
}
</script>

<template>
  <Box direction="col" class="profile-menu">
    <Box
      v-for="item in menuItems"
      :key="item.label"
      align="center"
      justify="between"
      gap="3"
      class="menu-item"
      role="button"
      tabindex="0"
      @click="handleItemActivate(item)"
      @keydown.enter="handleItemActivate(item)"
    >
      <Box align="center" gap="3">
        <Box align="center" justify="center" class="menu-item__icon">
          <PiIcon :name="item.icon" />
        </Box>
        <Box direction="col" gap="1">
          <Text type="body" weight="semibold" is="span">{{ item.label }}</Text>
          <Text type="caption" color="muted" is="span">{{ item.description }}</Text>
        </Box>
      </Box>
      <PiIcon name="chevron-right" class="menu-item__chevron" />
    </Box>

    <Box class="menu-divider" />

    <Box
      align="center"
      gap="3"
      class="menu-item menu-item--logout"
      role="button"
      tabindex="0"
      @click="emit('logout')"
      @keydown.enter="emit('logout')"
    >
      <Box align="center" justify="center" class="menu-item__icon menu-item__icon--danger">
        <PiIcon name="sign-out" />
      </Box>
      <Box direction="col" gap="1">
        <Text type="body" weight="semibold" color="danger" is="span">Log out</Text>
        <Text type="caption" color="muted" is="span">Sign out of your account</Text>
      </Box>
    </Box>
  </Box>
</template>

<style scoped>
.profile-menu {
  min-width: 260px;
}

.menu-item {
  padding: 0.75rem 0.875rem;
  cursor: pointer;
  border-radius: var(--p-border-radius-md);
  transition: background 0.15s ease;
}

.menu-item:hover {
  background: var(--p-primary-50);
}

.menu-item:focus-visible {
  outline: 2px solid var(--p-primary-500);
  outline-offset: -2px;
}

.menu-item__icon {
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background: var(--p-surface-100);
  color: var(--p-primary-700);
  flex-shrink: 0;
  font-size: 0.85rem;
}

.menu-item__icon--danger {
  background: var(--p-danger-50);
  color: var(--p-danger-600);
}

.menu-item__chevron {
  font-size: 0.75rem;
  color: var(--p-text-muted-color);
  flex-shrink: 0;
}

.menu-divider {
  height: 1px;
  background: var(--p-content-border-color);
  margin: 0.25rem 0.5rem;
}

.menu-item--logout:hover {
  background: var(--p-danger-50);
}
</style>
